/**
 * RTP Calculator for Palm Heist Scratch Card Game
 * 
 * This utility calculates the theoretical Return to Player (RTP) percentage
 * based on the game's symbol probabilities, payouts, and bonus features.
 */

/**
 * Calculate the RTP for the scratch card game
 * @param {Array} gameSymbols - Array of game symbols with weights and payouts
 * @param {Object} matchMultipliers - Multipliers based on number of matches
 * @param {Object} bonusFeatures - Bonus features with probabilities and payouts
 * @returns {Object} RTP calculation results
 */
export const calculateRTP = (gameSymbols, matchMultipliers, bonusFeatures) => {
  // Calculate total weight for probability calculations
  const totalWeight = gameSymbols.reduce((sum, s) => sum + s.weight, 0);
  
  // Calculate individual symbol probabilities
  const symbolProbabilities = gameSymbols.map(symbol => ({
    ...symbol,
    probability: symbol.weight / totalWeight
  }));
  
  // Calculate base RTP from regular symbol matches
  let baseRTP = 0;
  
  // Calculate probability of getting exactly 3 of each symbol
  // This is a simplification - in reality, the distribution is more complex
  for (const symbol of symbolProbabilities) {
    if (symbol.symbol === 'Wild' || symbol.symbol === 'Chest' || 
        symbol.symbol === 'Wheel' || symbol.symbol === 'Ticket') {
      continue; // Skip special symbols for base calculation
    }
    
    // Probability of getting exactly 3 of this symbol (binomial probability)
    // P(X=3) = (12 choose 3) * p^3 * (1-p)^9
    const p = symbol.probability;
    const binomialCoeff = 220; // 12 choose 3 = 220
    const probExactly3 = binomialCoeff * Math.pow(p, 3) * Math.pow(1-p, 9);
    
    // Expected return for this symbol with 3 matches
    const expectedReturn = probExactly3 * symbol.payout * matchMultipliers[3];
    baseRTP += expectedReturn;
  }
  
  // Calculate RTP from bonus features
  let bonusRTP = 0;
  let chestGameReturn = 0;
  let wheelReturn = 0;
  let lotteryReturn = 0;
  
  // Chest Game bonus (if exists)
  if (bonusFeatures.chestGame) {
    const chestGameProb = bonusFeatures.chestGame.probability;
    chestGameReturn = chestGameProb * bonusFeatures.chestGame.avgPayout;
    bonusRTP += chestGameReturn;
  }
  
  // Wheel of Fortune bonus (if exists)
  if (bonusFeatures.wheelOfFortune) {
    const wheelProb = bonusFeatures.wheelOfFortune.probability;
    wheelReturn = wheelProb * bonusFeatures.wheelOfFortune.avgPayout;
    bonusRTP += wheelReturn;
  }
  
  // Lottery Ticket bonus (if exists)
  if (bonusFeatures.lotteryTicket) {
    const lotteryProb = bonusFeatures.lotteryTicket.probability;
    
    for (const outcome of bonusFeatures.lotteryTicket.payouts) {
      lotteryReturn += lotteryProb * outcome.probability * outcome.multiplier;
    }
    
    bonusRTP += lotteryReturn;
  }
  
  // Calculate total RTP
  const totalRTP = baseRTP + bonusRTP;
  const rtpPercentage = totalRTP * 100;
  
  // For this game, we're targeting a 96% RTP
  const targetRTP = 96;
  
  return {
    baseRTP: baseRTP,
    bonusRTP: bonusRTP,
    totalRTP: totalRTP,
    rtpPercentage: rtpPercentage,
    targetRTP: targetRTP,
    breakdown: {
      baseGame: baseRTP * 100,
      chestBonus: chestGameReturn * 100,
      wheelBonus: wheelReturn * 100,
      lotteryBonus: lotteryReturn * 100
    }
  };
};

/**
 * Simulates the game many times to get an empirical RTP
 * @param {Function} generateCards - Function to generate a set of cards
 * @param {Function} calculateWin - Function to calculate win for a set of cards
 * @param {number} simulations - Number of simulations to run
 * @returns {number} Empirical RTP percentage
 */
export const simulateRTP = (generateCards, calculateWin, simulations = 10000) => {
  let totalBet = 0;
  let totalWin = 0;
  
  for (let i = 0; i < simulations; i++) {
    const cards = generateCards();
    const betAmount = 1; // Assuming 1 unit bet for simplicity
    const winAmount = calculateWin(cards, betAmount);
    
    totalBet += betAmount;
    totalWin += winAmount;
  }
  
  return (totalWin / totalBet) * 100;
};
